import { Box, Pagination, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { GridColDef, DataGrid, useGridApiRef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useState, useCallback } from "react";
import apiClient from "services/apiClient";
import TripOperationsRiskFilters from "./TripOperationsRiskFilters";
import { debounce } from "lodash";
import TripOperationsRiskRow from "./TripOperationsRiskRow";

const TripOperationsRisk = () => {
  const [operationsRisk, setOperationsRisk] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState<number>(0);
  const gridRef = useGridApiRef();

  const debounceSearch = useCallback(debounce(fetchData, 500), []);

  let initialSearch = {
    offset: 1,
    pageSize: 25,
    sortAsc: false,
    sortName: "operationsRiskLevel",
    note: "",
    clientIds: [],
    hubIds: [],
  };

  const [search, setSearch] = useState<any>(initialSearch);

  function fetchData(request) {
    setOperationsRisk([]);
    setIsLoading(true);
    apiClient
      .post(`/trips/operations-risk`, {
        hubIds: request.hubIds?.map((m) => m.id),
        clientIds: request.clientIds?.map((m) => m.id),
        riskNote: request.note,
        offset: request.offset - 1,
        pageSize: request.pageSize,
      })
      .then((results: any) => {
        setOperationsRisk(results.results);
        setTotal(results.count);
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (!search) return;
    debounceSearch({ ...search });
  }, [search]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  const getStartPage = () => {
    return (search.offset - 1) * search.pageSize + 1;
  };

  const getEndPage = () => {
    let end = search.offset * search.pageSize;
    return end > total ? total : end;
  };
  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      <table width="100%">
        <tbody>
          {[...Array(25)].map((_, index) => (
            <tr key={index}>
              <td key={index}>
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height="100px"
                  sx={{ my: 1, mx: 1, overflow: "hidden", borderRadius: "6px" }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  return (
    <>
      {!isLoading && (
        <Box sx={{ display: "flex", flexDirection: "row", justifyContent:"space-between"}}>
          <Box>
            <TripOperationsRiskFilters search={search} setSearch={setSearch} />
          </Box>
          <Box alignSelf={"flex-end"} >
            <Typography fontSize={"1em"}>{`${getStartPage()} - ${getEndPage()} of ${total}`} Results</Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {operationsRisk && (
              <>
                {operationsRisk.map((o, i) => (
                  <TripOperationsRiskRow key={o.id || i} dataItem={o} index={i} />
                ))}
                <Stack alignItems={"flex-end"}>
                  <Pagination
                    sx={{ justifyContent: "flex-end" }}
                    count={Math.ceil(total / search.pageSize)}
                    shape="rounded"
                    page={search.offset}
                    onChange={(e, page) => setSearch({ ...search, offset: page })}
                  />
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};
export default TripOperationsRisk;
